import { useState, useMemo } from "react";
import { useApp } from "../contexts/AppContext";
import { CLAN_DATA, TOTAL_FAMILIES } from "../data/landownerData";

/* ── helpers ─────────────────────────────────────────────────────── */
function initials(name) {
  return name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
}

/* ── Single member card ──────────────────────────────────────────── */
function MemberCard({ member, onClick }) {
  return (
    <div className="lo-pub-card" onClick={() => onClick(member)} title={member.name}>
      <div className="lo-pub-photo">
        {member.photo
          ? <img src={member.photo} alt={member.name} loading="lazy"/>
          : <div className="lo-pub-initials">{initials(member.name)}</div>
        }
      </div>
      <div className="lo-pub-name">{member.name}</div>
    </div>
  );
}

/* ── Family section ──────────────────────────────────────────────── */
function FamilySection({ familyName, members }) {
  const [open, setOpen] = useState(members.length > 0);
  const [preview, setPreview] = useState(null);

  return (
    <div className="lo-family-sec">
      <button className="lo-family-hdr" onClick={() => setOpen(o => !o)}>
        <span className="lo-fam-name">{familyName} Family</span>
        <span className="lo-fam-count">
          {members.length > 0
            ? `${members.length} member${members.length !== 1 ? "s" : ""}`
            : "Pending registration"
          }
        </span>
        <span className="lo-fam-chevron">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="lo-family-body">
          {members.length === 0 ? (
            <div className="lo-family-empty">
              <span>📋</span>
              <span>No members registered yet for this family.</span>
            </div>
          ) : (
            <div className="lo-pub-grid">
              {members.map(m => (
                <MemberCard key={m.id} member={m} onClick={setPreview}/>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {preview && (
        <div className="lo-lightbox" onClick={() => setPreview(null)}>
          <div className="lo-lb-card" onClick={e => e.stopPropagation()}>
            <div className="lo-lb-photo">
              {preview.photo
                ? <img src={preview.photo} alt={preview.name}/>
                : <div className="lo-lb-initials">{initials(preview.name)}</div>
              }
            </div>
            <div className="lo-lb-name">{preview.name}</div>
            <div className="lo-lb-meta">
              {preview.clan} · {preview.subclan} · {preview.family} Family
            </div>
            <button className="lo-lb-close" onClick={() => setPreview(null)}>✕ Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────── */
export default function LandownersPage() {
  const { landowners } = useApp();
  const [search, setSearch] = useState("");

  /* index landowners by clanId+family for fast lookup */
  const memberIndex = useMemo(() => {
    const idx = {};
    landowners.forEach(m => {
      const key = `${m.clanId}__${m.family}`;
      if (!idx[key]) idx[key] = [];
      idx[key].push(m);
    });
    return idx;
  }, [landowners]);

  const totalMembers = landowners.length;

  /* search */
  const searchResults = search.trim().length >= 2
    ? landowners.filter(m =>
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.family.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  return (
    <>
      {/* ── Hero ───────────────────────────────────────────────── */}
      <div className="lo-hero">
        <div className="eyebrow" style={{ color:"rgba(255,255,255,.65)" }}>
          Lomban Hydropower Development Association Inc.
        </div>
        <h1>Registered Landowner Members</h1>
        <p>
          Customary landowners of Portion 882C, Lomban Falls — Kaiyaul (Awain Tribe) &amp;
          Piau (Yanaitin Tribe) clans, Wabag District, Enga Province, PNG.
        </p>
        <div className="lo-hero-stats">
          <div className="lo-hstat">
            <div className="lo-hstat-num">{totalMembers}</div>
            <div className="lo-hstat-lbl">Registered Members</div>
          </div>
          <div className="lo-hstat-div"/>
          <div className="lo-hstat">
            <div className="lo-hstat-num">{TOTAL_FAMILIES}</div>
            <div className="lo-hstat-lbl">Registered Families</div>
          </div>
          <div className="lo-hstat-div"/>
          <div className="lo-hstat">
            <div className="lo-hstat-num">2</div>
            <div className="lo-hstat-lbl">Clans</div>
          </div>
          <div className="lo-hstat-div"/>
          <div className="lo-hstat">
            <div className="lo-hstat-num">5</div>
            <div className="lo-hstat-lbl">Subclans</div>
          </div>
        </div>
      </div>

      {/* ── Search ─────────────────────────────────────────────── */}
      <div style={{ background:"var(--c)", padding:"2rem 1.5rem", borderBottom:"1px solid var(--br)" }}>
        <div className="container" style={{ maxWidth: 700 }}>
          <input
            className="lo-search-pub"
            placeholder="🔍  Search by member name or family name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search.trim().length >= 2 && (
            <div className="lo-search-dropdown">
              {searchResults.length === 0
                ? <div className="lo-sdr-empty">No results for "{search}"</div>
                : searchResults.map(m => (
                    <div key={m.id} className="lo-sdr-row">
                      <div className="lo-sdr-photo">
                        {m.photo
                          ? <img src={m.photo} alt={m.name}/>
                          : <div className="lo-sdr-init">{initials(m.name)}</div>
                        }
                      </div>
                      <div>
                        <div style={{ fontWeight:700, fontSize:".9rem" }}>{m.name}</div>
                        <div style={{ fontSize:".75rem", color:"var(--gr)" }}>
                          {m.clan} · {m.subclan} · <strong>{m.family}</strong> Family
                        </div>
                      </div>
                    </div>
                  ))
              }
            </div>
          )}
        </div>
      </div>

      {/* ── Clan sections ──────────────────────────────────────── */}
      {CLAN_DATA.map((clan, ci) => (
        <section key={clan.id}
          className={`lo-clan-sec${ci % 2 === 1 ? " lo-clan-alt" : ""}`}>
          <div className="container">

            {/* Clan banner */}
            <div className={`lo-clan-banner lo-clan-${clan.id}`}>
              <div>
                <div className="lo-clan-title">{clan.name}</div>
                <div className="lo-clan-tribe">{clan.tribe}</div>
              </div>
              <div className="lo-clan-loc">📍 {clan.location}</div>
            </div>

            {/* Subclans */}
            {clan.subclans.map(sub => {
              const subTotal = sub.families.reduce((acc, f) =>
                acc + (memberIndex[`${clan.id}__${f}`]?.length ?? 0), 0);
              return (
                <div key={sub.id} className="lo-subclan-block">
                  <div className="lo-subclan-hdr">
                    <span className="lo-subclan-name">{sub.name}</span>
                    <span className="lo-subclan-meta">
                      {sub.families.length} families · {subTotal} registered member{subTotal !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {sub.families.map(f => (
                    <FamilySection
                      key={f}
                      familyName={f}
                      members={memberIndex[`${clan.id}__${f}`] ?? []}
                    />
                  ))}
                </div>
              );
            })}

          </div>
        </section>
      ))}

      {/* ── Footer note ────────────────────────────────────────── */}
      <div style={{ background:"var(--gd)", color:"rgba(255,255,255,.7)", textAlign:"center",
                    padding:"2rem 1.5rem", fontSize:".82rem", lineHeight:1.7 }}>
        <strong style={{ color:"#fff" }}>LHDA Inc. — Inc. No. 5-105293</strong><br/>
        Representing the Customary Landowners of Lomban Falls on the Lai River,<br/>
        Wabag District, Enga Province, Papua New Guinea.
      </div>
    </>
  );
}
